import { SocketIo } from "App";
import ChallengeTabs from "Components/ChallengeTabs/ChallengeTabs";
import StageChange from "Components/StageChange/StageChange";
import { Skeleton } from "Components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "Components/ui/table";
import { motion, useScroll } from "framer-motion";
import UseInnerWidth from "hooks/UseInnerWidth";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import danger from "../../Assets/Images/danger.svg";
import usd from "../../Assets/Images/usd.svg";
import {
  getClosedTrades,
  getOpenTrades,
} from "../../Redux/ChallengeTrades/ChallengeTradesActions";
import "./ChallengeTrades.scss";
import { Separator } from "Components/ui/separator";
import { Tickets } from "lucide-react";
import { Card } from "Components/ui/card";
import mt5Icon from "Assets/Images/mt5icon.svg";
import tradelockerIcon from "Assets/Images/tradelockerIcon.svg";
import { useAuth } from "@clerk/clerk-react";
import { useTheme } from "hooks/ThemeContext";
import TradeLockerBlack from "Assets/Images/TradeLockerBlack.svg";

function ChallengeTrades() {
  const { theme } = useTheme();
  const scrollRef = useRef(null);

  const { challengeStageInfo, challengeStageInfoLoading } = useSelector(
    (state) => state.ChallengeOverviewReducer
  );

  // Attach the scrollRef to the container
  const { scrollYProgress } = useScroll({ container: scrollRef });
  const [opacity, setOpacity] = useState(1);

  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (value) => {
      setOpacity(1 - value); // Example of setting opacity based on scroll
    });
    return unsubscribe;
  }, [scrollYProgress]);

  const [data, setData] = useState([]);
  const [selectedTab, setSelectedTab] = useState("Open");
  const dispatch = useDispatch();

  const { getToken } = useAuth();

  const {
    openTrades,
    getOpenTradesLoading,
    closedTrades,
    getClosedTradesLoading,
  } = useSelector((state) => state.ChallengeTradesReducer);

  useEffect(() => {
    if (challengeStageInfo?.Account?.login) {
      if (selectedTab === "Open" && challengeStageInfo?.Account?.login) {
        dispatch(
          getOpenTrades(
            getToken,
            encodeURIComponent(challengeStageInfo?.Account?.login)
          )
        );
      } else {
        dispatch(
          getClosedTrades(
            getToken,
            encodeURIComponent(challengeStageInfo?.Account?.login)
          )
        );
      }
    }
  }, [selectedTab, challengeStageInfo]);

  useEffect(() => {
    if (selectedTab === "Open") {
      setData(openTrades);
    } else {
      setData(closedTrades);
    }
  }, [openTrades, closedTrades]);

  const navigate = useNavigate();

  const { id } = useParams();

  const { scrollValue, scrollable } = useSelector(
    (state) => state.ScrollVariablesSlice
  );

  useEffect(() => {
    if (selectedTab === "Open" && challengeStageInfo?.Account?.login) {
      if (getOpenTradesLoading) return;
      SocketIo?.on(
        `onPositionChange_${challengeStageInfo?.Account?.login}`,
        (socketData) => {
          const index = data?.findIndex(
            (item) => item.Ticket === socketData?.Ticket
          );
          if (index !== -1) {
            if (socketData?.Action === "Delete") {
              setData((prev) => {
                const newData = [...prev];
                newData.splice(index, 1);
                return newData;
              });
            } else
              setData((prev) => {
                const newData = [...prev];
                newData[index] = {
                  ...newData[index],
                  Profit: socketData?.Profit,
                };
                return newData;
              });
          } else {
            if (socketData?.Action === "Add") {
              setData((prev) => {
                return [...prev, socketData];
              });
            }
          }
        }
      );
    } else {
      SocketIo?.off(`onPositionChange_${challengeStageInfo?.Account?.login}`);
    }
    return () => {
      SocketIo?.off(`onPositionChange_${challengeStageInfo?.Account?.login}`);
    };
  }, [selectedTab, getOpenTradesLoading, data]);

  const [counter, setCounter] = useState(0);

  const width = UseInnerWidth();

  return (
    <>
      {challengeStageInfo?.status == "BREACHED" && (
        <div className="flex items-center gap-4 bg-red-opacity w-full p-4 rounded-lg  mb-4">
          <img src={danger} className="w-6 h-6" alt="danger" />
          <div>
            <div className="flex flex-col gap-2 text-red">
              Disqualification Notice: Rule Breached
            </div>
            <div className="text-red opacity-50">
              Reason: {challengeStageInfo?.breachReason}
              <span className="text-grey  text-xs">
                {"("}
                {new Date(challengeStageInfo?.updatedAt).toLocaleDateString(
                  "en-US",
                  {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  }
                )}
                {")"}
              </span>
            </div>
          </div>
        </div>
      )}
      <div
        className=" w-full pb-[15rem]"
        style={{
          maxWidth: "calc(100vw - 3rem)",
        }}
      >
        <div className="flex flex-col gap-4">
          <div className="flex sm:items-center justify-between sm:flex-row flex-col ">
            <div className="flex items-baseline gap-3 w-fit text-nowrap">
              <div className="text-2xl font-N_Medium flex flex-row gap-4 item-center">
                {!challengeStageInfoLoading &&
                  (challengeStageInfo?.Challenge?.Platform?.name?.toLowerCase() ===
                  "platform 5" ? (
                    <img src={mt5Icon} alt="mt5" className="w-[0.95rem]" />
                  ) : (
                    <img
                      src={
                        theme === "light" ? TradeLockerBlack : tradelockerIcon
                      }
                      alt="tradelockerIcon"
                      className="w-[1.05rem]"
                    />
                  ))}
                Challenge {!challengeStageInfoLoading && "-"}{" "}
                {challengeStageInfo?.Account?.login}{" "}
              </div>
            </div>
            <div className="sm:my-0 my-4">
              <StageChange />
            </div>
          </div>{" "}
          <ChallengeTabs page="trades" />
        </div>
        <div className="flex flex-row gap-8 items-center mb-6 mt-6">
          <div
            className={`cursor-pointer font-N_Medium text-lg ${
              selectedTab === "Open"
                ? "text-primary underline underline-offset-8"
                : "text-white-opacity"
            }`}
            onClick={() => setSelectedTab("Open")}
          >
            Open
          </div>
          <div
            className={`cursor-pointer font-N_Medium text-lg ${
              selectedTab === "Closed"
                ? "text-primary underline underline-offset-8"
                : "text-white-opacity"
            }`}
            onClick={() => setSelectedTab("Closed")}
          >
            Closed
          </div>
        </div>

        {width > 600 ? (
          <div className="relative">
            <div
              ref={scrollRef}
              className="rounded-2xl bg-background p-5 max-h-[70vh] overflow-auto relative hideScrollBar"
            >
              {getClosedTradesLoading || getOpenTradesLoading ? (
                <div className="space-y-3 w-full pb-4">
                  <div className="flex items-center gap-4 w-full p-4 pb-0">
                    <div className="w-full text-sm opacity-30">Asset</div>
                    <div className="w-full text-sm opacity-30">Date</div>
                    <div className="w-full text-sm opacity-30">Buy/Sell</div>
                    <div className="w-full text-sm opacity-30">Size</div>
                    <div className="w-full text-sm opacity-30">Open Price</div>
                    <div className="w-full text-sm opacity-30">Close Price</div>
                    <div className="w-full text-sm opacity-30">P&L</div>
                  </div>
                  {[...Array(5)].map((_, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 w-full px-4 py-2"
                    >
                      <Skeleton className="flex-1 h-[30px]" />
                      <Skeleton className="flex-1 h-[30px]" />
                      <Skeleton className="flex-1 h-[30px]" />
                      <Skeleton className="flex-1 h-[30px]" />
                      <Skeleton className="flex-1 h-[30px]" />
                      <Skeleton className="flex-1 h-[30px]" />
                      <Skeleton className="flex-1 h-[30px]" />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <Table className="border-separate border-spacing-y-3 border-spacing-x-0 hideScrollBar">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Asset</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Buy/Sell</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Open Price</TableHead>
                        <TableHead>Close Price</TableHead>
                        <TableHead>P&L</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data?.length > 0 ? (
                        data.map((item) => (
                          <TableRow
                            key={item.id}
                            className="bg-background-secondary rounded-full"
                          >
                            <TableCell className="rounded-l-2xl">
                              <div className="flex flex-row items-center gap-2">
                                <img src={usd} alt="usd" className="w-[30px]" />
                                {item.Symbol}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                              </div>
                            </TableCell>
                            <TableCell>
                              {new Date(
                                selectedTab === "Open"
                                  ? item?.openTime
                                  : item?.closeTime
                              ).toLocaleDateString("en-US", {
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              })}
                              <br />
                              {new Date(
                                selectedTab === "Open"
                                  ? item?.openTime
                                  : item?.closeTime
                              ).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                                hour12: false,
                              })}
                            </TableCell>
                            <TableCell
                              className={`${
                                item?.Type?.toLowerCase() === "buy"
                                  ? "text-green"
                                  : "text-primary"
                              }`}
                            >
                              {item.Type.charAt(0).toUpperCase() +
                                item.Type.slice(1).toLowerCase()}
                            </TableCell>
                            <TableCell>{item.lots}</TableCell>
                            <TableCell>
                              {Number(item?.PriceOpen)?.toLocaleString(
                                "en-US",
                                {
                                  style: "currency",
                                  currency: "USD",
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 3,
                                }
                              )}
                            </TableCell>
                            <TableCell>
                              {selectedTab === "Open"
                                ? "-"
                                : Number(item?.PriceClose)?.toLocaleString(
                                    "en-US",
                                    {
                                      style: "currency",
                                      currency: "USD",
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 2,
                                    }
                                  )}
                            </TableCell>
                            <TableCell
                              className={`rounded-r-2xl ${
                                item?.Profit > 0 ? "text-green" : "text-red"
                              }`}
                            >
                              {item?.Profit > 0 && "+"}
                              {item?.Profit?.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell className="text-grey">
                            No Data Found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </>
              )}
            </div>

            <motion.div
              className={`absolute bottom-0 ${
                theme === "dark" && "gradient_overlay"
              } w-full h-[10rem] rounded-2xl`}
              style={{ opacity, display: opacity === 0 ? "none" : "block" }}
            ></motion.div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {getClosedTradesLoading || getOpenTradesLoading ? (
              <>
                <Card className="p-4 space-y-4 border-none bg-background-tradeLocker">
                  <div className="flex justify-between items-center">
                    <Skeleton className="h-4 w-10" />
                    <Skeleton className="h-4 w-32" />
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-8" />
                    </div>

                    <div className="flex justify-between items-center">
                      <Skeleton className="h-4 w-8" />
                      <Skeleton className="h-4 w-8" />
                    </div>

                    <div className="flex justify-between items-center">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-12" />
                    </div>

                    <div className="flex justify-between items-center">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-12" />
                    </div>

                    <div className="flex justify-between items-center">
                      <Skeleton className="h-4 w-8" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </div>
                </Card>
                <Card className="p-4 space-y-4 border-none bg-background-tradeLocker">
                  <div className="flex justify-between items-center">
                    <Skeleton className="h-4 w-10" />
                    <Skeleton className="h-4 w-32" />
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-8" />
                    </div>

                    <div className="flex justify-between items-center">
                      <Skeleton className="h-4 w-8" />
                      <Skeleton className="h-4 w-8" />
                    </div>

                    <div className="flex justify-between items-center">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-12" />
                    </div>

                    <div className="flex justify-between items-center">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-12" />
                    </div>

                    <div className="flex justify-between items-center">
                      <Skeleton className="h-4 w-8" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </div>
                </Card>
              </>
            ) : data?.length > 0 ? (
              data?.map((item, index) => {
                return (
                  <div
                    className="bg-background-tradeLocker p-6 rounded-lg"
                    key={index}
                  >
                    <div className="flex flex-row gap-4 items-center justify-between flex-wrap">
                      <div className="text-lg font-N_Bold">{item.Symbol}</div>
                      <div className="text-lg font-N_Light">
                        {new Date(
                          selectedTab === "Open"
                            ? item?.openTime
                            : item?.closeTime
                        ).toLocaleDateString("en-US", {
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })}{" "}
                        at{" "}
                        {new Date(
                          selectedTab === "Open"
                            ? item?.openTime
                            : item?.closeTime
                        ).toLocaleTimeString("en-US", {
                          hour: "numeric",
                          minute: "numeric",
                          hour12: true,
                        })}
                      </div>
                    </div>
                    <Separator className={"opacity-[0.07] bg-white my-4"} />
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-row items-center gap-4 justify-between">
                        <div className="opacity-[0.3] font-N_Light text-lg">
                          Buy/Sell
                        </div>
                        <div
                          className={`text-lg font-N_Medium ${
                            item?.Type?.toLowerCase() === "buy"
                              ? "text-green"
                              : "text-primary"
                          }`}
                        >
                          {" "}
                          {item.Type.charAt(0).toUpperCase() +
                            item.Type.slice(1).toLowerCase()}
                        </div>
                      </div>
                      <div className="flex flex-row items-center gap-4 justify-between">
                        <div className="opacity-[0.3] font-N_Light text-lg">
                          Size
                        </div>
                        <div className={`text-lg font-N_Medium`}>
                          {item.lots}
                        </div>
                      </div>
                      <div className="flex flex-row items-center gap-4 justify-between">
                        <div className="opacity-[0.3] font-N_Light text-lg">
                          Open Price
                        </div>
                        <div className={`text-lg font-N_Medium`}>
                          {Number(item?.PriceOpen)?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 3,
                          })}
                        </div>
                      </div>
                      <div className="flex flex-row items-center gap-4 justify-between">
                        <div className="opacity-[0.3] font-N_Light text-lg">
                          Close Price
                        </div>
                        <div className={`text-lg font-N_Medium`}>
                          {selectedTab === "Open"
                            ? "-"
                            : Number(item?.PriceClose)?.toLocaleString(
                                "en-US",
                                {
                                  style: "currency",
                                  currency: "USD",
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                }
                              )}
                        </div>
                      </div>
                      <div className="flex flex-row items-center gap-4 justify-between">
                        <div className="opacity-[0.3] font-N_Light text-lg">
                          P&L
                        </div>
                        <div
                          className={`text-lg font-N_Medium ${
                            item?.Profit > 0 ? "text-green" : "text-primary"
                          }`}
                        >
                          {item?.Profit > 0 && "+"}
                          {item?.Profit?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex gap-2 items-center flex-col text-white-opacity justify-center w-full m-auto text-center mt-[8rem]">
                <Tickets size={"2rem"} />
                <div className="font-N_Bold">No Trades Found</div>
                <div className="font-N_Thin -mt-2">
                  Start trading to see your trades here!
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default ChallengeTrades;
